import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["expanded"];
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import React, { useCallback, useRef } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { ExpandIconWrapper, ExpandLayoutWrapperWithRef, expandMessages, WidthProvider } from '@atlaskit/editor-common/ui';
import { akEditorLineHeight, akEditorSwoopCubicBezier, akLayoutGutterOffset } from '@atlaskit/editor-shared-styles';
import { default as ChevronRightIconLegacy } from '@atlaskit/icon/glyph/chevron-right';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import Tooltip from '@atlaskit/tooltip';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import _uniqueId from 'lodash/uniqueId';
import { injectIntl } from 'react-intl-next';
import { MODE, PLATFORM } from '../analytics/events';
import { ActiveHeaderIdConsumer } from './active-header-id-provider';
var titleStyles = css({
  outline: 'none',
  border: 'none',
  // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
  fontSize: "".concat(14 / 16, "rem"),
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
  lineHeight: akEditorLineHeight,
  fontWeight: "var(--ds-font-weight-regular, 400)",
  display: 'flex',
  flex: 1,
  margin: 0,
  padding: "0 0 0 ".concat("var(--ds-space-050, 4px)"),
  textAlign: 'left'
});
var containerStyles = css({
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: "var(--ds-border-radius-100, 4px)",
  minHeight: '25px',
  background: "var(--ds-background-neutral-subtle, transparent)",
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  transition: "background 0.3s ".concat(akEditorSwoopCubicBezier, ", border-color 0.3s ").concat(akEditorSwoopCubicBezier),
  padding: "var(--ds-space-0, 0px)",
  paddingBottom: "var(--ds-space-0, 0px)",
  marginTop: "var(--ds-space-050, 0.25rem)",
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  'td > :not(style):first-child, td > style:first-child + *': {
    marginTop: 0
  }
});
var containerStylesExpanded = css({
  background: "var(--ds-surface, rgba(255, 255, 255, 0.6))",
  paddingBottom: "var(--ds-space-100, 8px)",
  borderColor: "var(--ds-border, #091E4224)"
});
var containerStylesFocused = css({
  borderColor: "var(--ds-border-focused, #388BFF)"
});
var containerStylesDataNodeTypeExpand = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
  marginLeft: "-".concat(akLayoutGutterOffset, "px"),
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
  marginRight: "-".concat(akLayoutGutterOffset, "px")
});
var titleContainerStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  background: 'none',
  border: 'none',
  // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
  fontSize: "".concat(14 / 16, "rem"),
  width: '100%',
  color: "var(--ds-text-subtle, #44546F)",
  overflow: 'hidden',
  cursor: 'pointer',
  padding: "var(--ds-space-100, 8px)",
  '&:focus': {
    outline: 0
  }
});
var titleContainerStylesExpanded = css({
  paddingBottom: "var(--ds-space-0, 0px)"
});
var contentContainerStyles = css({
  paddingTop: "var(--ds-space-0, 0px)",
  marginLeft: "var(--ds-space-050, 4px)",
  paddingRight: "var(--ds-space-200, 16px)",
  paddingLeft: "var(--ds-space-400, 32px)",
  display: 'flow-root',
  visibility: 'hidden',
  // The follow rules inside @supports block are added as a part of ED-8893
  // The fix is targeting mobile bridge on iOS 12 or below,
  // We should consider remove this fix when we no longer support iOS 12
  '@supports not (display: flow-root)': {
    width: '100%',
    boxSizing: 'border-box'
  }
});
var contentContainerStylesExpanded = css({
  paddingTop: "var(--ds-space-100, 8px)",
  visibility: 'visible'
});
var contentContainerStylesNotExpanded = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
  '.expand-content-wrapper, .nestedExpand-content-wrapper': {
    /* We visually hide the content here to preserve the content during copy+paste */
    /* Do not add text nowrap here because inline comment navigation depends on the location of the text */
    width: '100%',
    display: 'block',
    height: 0,
    overflow: 'hidden',
    clip: 'rect(1px, 1px, 1px, 1px)',
    userSelect: 'none'
  }
});
var clearNextSiblingMarginTopStyle = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  '& + *': {
    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview, @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
    marginTop: '0 !important'
  }
});
var Container = function Container(props) {
  return jsx("div", _extends({
    css: [containerStyles, props['data-node-type'] === 'expand' && containerStylesDataNodeTypeExpand, props.expanded && containerStylesExpanded, props.focused && containerStylesFocused]
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, props), props.children);
};
var TitleContainer = function TitleContainer(props) {
  var expanded = props.expanded,
    buttonProps = _objectWithoutProperties(props, _excluded);
  return (
    // eslint-disable-next-line @atlaskit/design-system/no-html-button
    jsx("button", _extends({
      type: "button",
      css: [titleContainerStyles, expanded && titleContainerStylesExpanded]
      // Ignored via go/ees005
      // eslint-disable-next-line react/jsx-props-no-spreading
    }, buttonProps), props.children)
  );
};
TitleContainer.displayName = 'TitleContainerButton';
var ContentContainer = function ContentContainer(props) {
  return jsx("div", _extends({
    css: [contentContainerStyles, props.expanded && contentContainerStylesExpanded, !props.expanded && contentContainerStylesNotExpanded]
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, props), props.children);
};
function fireExpandToggleAnalytics(nodeType, expanded, fireAnalyticsEvent) {
  if (!fireAnalyticsEvent) {
    return;
  }
  fireAnalyticsEvent({
    action: ACTION.TOGGLE_EXPAND,
    actionSubject: nodeType === 'expand' ? ACTION_SUBJECT.EXPAND : ACTION_SUBJECT.NESTED_EXPAND,
    attributes: {
      platform: PLATFORM.WEB,
      mode: MODE.RENDERER,
      expanded: !expanded
    },
    eventType: EVENT_TYPE.TRACK
  });
}
function Expand(_ref) {
  var title = _ref.title,
    children = _ref.children,
    nodeType = _ref.nodeType,
    intl = _ref.intl,
    fireAnalyticsEvent = _ref.fireAnalyticsEvent,
    nestedHeaderIds = _ref.nestedHeaderIds,
    rendererAppearance = _ref.rendererAppearance;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    expanded = _React$useState2[0],
    setExpanded = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    focused = _React$useState4[0],
    setFocused = _React$useState4[1];
  var isMobile = false;
  var label = intl.formatMessage(expanded ? expandMessages.collapseNode : expandMessages.expandNode);
  var _useRef = useRef(_uniqueId('expand-title-')),
    id = _useRef.current;
  var handleFocus = useCallback(function () {
    return setFocused(true);
  }, []);
  var handleBlur = useCallback(function () {
    return setFocused(false);
  }, []);
  return jsx(Container, {
    "data-testid": "expand-container-".concat(nodeType, "-").concat(id),
    "data-node-type": nodeType,
    "data-title": title,
    "data-expanded": expanded,
    expanded: expanded,
    focused: focused
  }, nestedHeaderIds && nestedHeaderIds.length > 0 ? jsx(ActiveHeaderIdConsumer, {
    nestedHeaderIds: nestedHeaderIds,
    onNestedHeaderIdMatch: function onNestedHeaderIdMatch() {
      return setExpanded(true);
    }
  }) : null, jsx(TitleContainer, {
    onClick: function onClick(e) {
      e.preventDefault();
      e.stopPropagation();
      fireExpandToggleAnalytics(nodeType, expanded, fireAnalyticsEvent);
      setExpanded(!expanded);
      e.persist();
      // @ts-ignore detail doesn't exist on type
      e.detail ? handleBlur() : handleFocus();
    },
    onFocus: handleFocus,
    onBlur: handleBlur,
    "aria-labelledby": id,
    "aria-expanded": expanded,
    contentEditable: false,
    expanded: expanded
  }, isMobile ? jsx(ExpandIconWrapper, {
    expanded: expanded
  }, jsx(ChevronRightIcon, {
    label: label,
    LEGACY_fallbackIcon: ChevronRightIconLegacy,
    spacing: "spacious"
  })) : jsx(Tooltip, {
    content: label,
    position: "top"
    // @ts-ignore: [PIT-1685] Fails in post-office due to backwards incompatibility issue with React 18
    ,
    tag: ExpandLayoutWrapperWithRef,
    testId: 'tooltip'
  }, jsx(ExpandIconWrapper, {
    expanded: expanded
  }, jsx(ChevronRightIcon, {
    label: label,
    LEGACY_fallbackIcon: ChevronRightIconLegacy,
    spacing: "spacious"
  }))), jsx("span", {
    css: titleStyles,
    id: id
  }, title || intl.formatMessage(expandMessages.expandDefaultTitle))), jsx(ContentContainer, {
    expanded: expanded
  }, jsx("div", {
    className: "".concat(nodeType, "-content-wrapper")
  }, jsx(WidthProvider, null, jsx("div", {
    css: clearNextSiblingMarginTopStyle
  }), editorExperiment('confluence_p2m_style_recalc_and_expand_joint_exp', true, {
    exposure: true
  }) ? expanded && children : children))));
}
export default injectIntl(Expand);